<template>
  <div class="subpage-wrapper">
    <transition name="fade">
      <div class="subpage-container" v-if="show">
        <div class="inner-container">
          <div class="type-selector-container">
            <div class="pack-select-wrapper">
              <PromptPackCustomSelect
                :key="selectKey"
                v-model="selectedPromptPack"
                v-on:input="getPromptsFromPack"
                :options="promptPacks"
                :default="selectedPromptPack"
                :disabled="isSubmittingPrompt"
                noOptionsText="-- None --"
                @click="stopCreatingPrompt"
              />
            </div>

            <SpinnerButton
              class="button delete-pack-button"
              :isDisabled="selectedPromptPack.promptPackId === null"
              :isProcessing="isSubmittingPrompt"
              :showSpinner="false"
              @click="showDeletePackModal = true; stopCreatingPrompt();"
            >
              Delete Prompt Pack
            </SpinnerButton>

            <SpinnerButton
              class="button create-pack-button"
              style="margin-left: 0;"
              :isDisabled="selectedPromptPack.promptPackId === null"
              :isProcessing="isSubmittingPrompt"
              :showSpinner="false"
              @click="showRenamePackModal = true; stopCreatingPrompt();"
            >
              Rename Prompt Pack
            </SpinnerButton>

            <SpinnerButton
              class="button create-pack-button"
              :isProcessing="isSubmittingPrompt"
              :showSpinner="false"
              @click="showCreatePackModal = true; stopCreatingPrompt();"
            >
              + Create New Prompt Pack
            </SpinnerButton>

          </div>
          <div
            class="main-prompt-message-container"
            v-show="selectedPromptPack.promptPackId === null"
          >
            <h1>You haven't created a prompt pack yet</h1>
            <h3>Prompt packs can be created by anyone on your team for different activities such as executive meetings, company off-sites, recruiting, employee training, etc. </h3>
            <h3>If you’re looking for more ideas or inspiration, get in touch!</h3>
            <h3>You can reach one of our prompt experts at <a href="mailto:support@weavr.cc">support@weavr.cc</a></h3>
          </div>
          <div 
            ref="promptListContainer"
            class="prompt-list-container"
            v-show="selectedPromptPack.promptPackId"
          >
            <div class="prompt-list" v-show="showPack">
              <div :class="[{ 'prompt-item-property-hidden': isCreatingPrompt || isEditingPrompt }, 'prompt-item', 'top-row']" >
                <div class="prompt-item-property-text"> Prompt </div>
                <div class="prompt-item-property-type"> Actions </div>
                <div class="prompt-item-property-type"> Set </div>
                <div class="prompt-item-property-type"> Reply Type </div>
                <div class="prompt-item-property-type"> Last Edited </div>
                <div class="prompt-item-property-type"> Active </div>
                <div class="prompt-item-property-overflow"></div>
              </div>
              <div class="prompt-item" v-show="!isCreatingPrompt">
                <SpinnerButton
                  class="prompt-item-property-text prompt-item-to-create"
                  :isProcessing="isSubmittingPrompt"
                  :showSpinner="false"
                  @click="startCreatingPrompt"
                >
                  + Type in a new prompt to add
                </SpinnerButton>
              </div>
              <div 
                class="prompt-item"
                v-show="isCreatingPrompt"
              >
                <div class="prompt-item-property-text">
                  <input
                    ref="createTextInput"
                    class="create-custom-prompt-to-create-text static"
                    v-model="customPromptToCreate.text"
                    :disabled="isSubmittingPrompt"
                    placeholder="Type in your prompt here..."
                    @keydown.enter.prevent="createCustomPromptInPack"
                  />
                  <span :class="[{ invalid: customPromptToCreate.text.length > characterLimit }, 'create-custom-prompt-character-limit']">
                    {{ customPromptToCreate.text.length }} / {{ characterLimit }} characters
                  </span>
                </div>
                <div class="prompt-item-property-type"> Custom </div>
                <div class="prompt-item-property-type">
                  <CustomSelect
                    ref="customPromptReplySelector"
                    v-model="customPromptToCreate.reply"
                    :options="['text', 'gif']"
                    :disabled="isSubmittingPrompt"
                  />
                </div>
                <div class="prompt-item-property-create-custom button">
                  <SpinnerButton
                    class="create-custom-prompt-button"
                    :isDisabled="customPromptToCreate.text.length < 1 || customPromptToCreate.text.length > characterLimit"
                    :isProcessing="isSubmittingPrompt"
                    @click="createCustomPromptInPack"
                  >
                    Create Prompt
                  </SpinnerButton>
                </div>
                <div class="prompt-item-property-cancel-custom button">
                  <SpinnerButton
                    class="create-custom-prompt-cancel-button"
                    :isProcessing="isSubmittingPrompt"
                    :showSpinner="false"
                    @click="stopCreatingPrompt"
                  >
                    Cancel
                  </SpinnerButton>
                </div>
              </div>
              <div
                class="prompt-item"
                v-for="(prompt, promptIndex) in promptPackCustomPrompts"
                :key="'c'+promptIndex"
              >
                <div 
                  :class="[{ 'inactive': !prompt.active }, 'prompt-item-property-text']"
                  v-show="!prompt.hasOwnProperty('isEditing') || prompt.isEditing === false"  
                >
                  {{ transformPromptText(prompt.text) }} 
                </div>
                <div 
                  class="prompt-item-property-type"
                  v-show="!prompt.hasOwnProperty('isEditing') || prompt.isEditing === false"  
                >
                  <SpinnerButton
                    class="prompt-item-button edit"
                    :isProcessing="isSubmittingPrompt"
                    :showSpinner="false"
                    @click="editCustomPrompt(promptIndex)"
                  >
                    Edit
                  </SpinnerButton>
                </div>
                <div 
                  :class="[{ 'inactive': !prompt.active }, 'prompt-item-property-type']"
                  v-show="!prompt.hasOwnProperty('isEditing') || prompt.isEditing === false"  
                >
                  {{ parsePromptSetName(prompt.set) }}
                </div>
                <div 
                  :class="[{ 'inactive': !prompt.active }, 'prompt-item-property-type']"
                  v-show="!prompt.hasOwnProperty('isEditing') || prompt.isEditing === false"  
                >
                  {{ prompt.reply }} 
                </div>
                <div 
                  :class="[{ 'inactive': !prompt.active }, 'prompt-item-property-type']"
                  v-show="!prompt.hasOwnProperty('isEditing') || prompt.isEditing === false"  
                >
                  {{ new Date(prompt.lastEditedTimestamp).toLocaleString('default', { month: 'short', day: 'numeric', year: 'numeric' }) }}
                  <br>
                  {{ prompt.editedByFirstName }}
                </div>
                <toggle-button
                  :value="false"
                  v-show="!prompt.hasOwnProperty('isEditing') || prompt.isEditing === false"
                  color="#00B4A3"
                  :sync="true"
                  :height="toggleHeight"
                  :width="toggleWidth"
                  v-model="prompt.active"
                  :disabled="isSubmittingPrompt"
                  class="prompt-item-property-type"
                  style="display: flex; align-items: center;"
                  @change="togglePromptActivity('CUSTOM', promptPackCustomPrompts, promptIndex)"
                />
                <div 
                  class="prompt-item-property-overflow"
                  v-show="!prompt.hasOwnProperty('isEditing') || prompt.isEditing === false"  
                >
                  <PromptItemOverflow 
                    ref="customOverflow"
                    @opened="stopCreatingPrompt(); stopEditingCustomPrompt(promptIndexBeingEdited);"
                    :disabled="isSubmittingPrompt"
                  >
                    <template v-slot:dropdown>
                      <ul class="dropdown">
                        <li>
                          <a @click="$refs['customOverflow'][promptIndex].close(); duplicatePrompt(promptPackCustomPrompts, promptIndex);">
                            Duplicate
                          </a>
                        </li>
                        <li>
                          <a @click="$refs['customOverflow'][promptIndex].close(); removePromptFromPack(promptPackCustomPrompts, promptIndex);" 
                          class="text-danger">
                            Remove
                          </a>
                        </li>
                      </ul>
                    </template>
                  </PromptItemOverflow>
                </div>
                <div 
                  class="prompt-item-property-text no-overflow"
                  v-show="prompt.hasOwnProperty('isEditing') && prompt.isEditing === true"
                >
                  <input
                    ref="customTextInput"
                    class="create-custom-prompt-to-create-text static"
                    v-model="prompt.text"
                    :disabled="isSubmittingPrompt"
                    @keydown.enter.prevent="updateCustomPromptInPack(promptPackCustomPrompts, promptIndex)"
                  />
                  <span :class="[{ invalid: prompt.text.length > characterLimit }, 'create-custom-prompt-character-limit']">
                    {{ prompt.text.length }} / {{ characterLimit }} characters
                  </span>
                </div>
                <div 
                  class="prompt-item-property-type"
                  v-show="prompt.hasOwnProperty('isEditing') && prompt.isEditing === true"
                > 
                  Custom 
                </div>
                <div 
                  class="prompt-item-property-type"
                  v-show="prompt.hasOwnProperty('isEditing') && prompt.isEditing === true"
                >
                  <CustomSelect
                    ref="customReplySelect"
                    v-model="prompt.reply"
                    :options="['text', 'gif']"
                    :default="prompt.reply"
                    :disabled="isSubmittingPrompt"
                  />
                </div>
                <div
                  class="prompt-item-property-create-custom button"
                  v-show="'isEditing' in prompt && prompt.isEditing === true"
                >
                  <SpinnerButton
                    class="create-custom-prompt-button"
                    :isProcessing="isSubmittingPrompt"
                    :isDisabled="prompt.text.length < 1 || prompt.text.length > characterLimit"
                    @click="updateCustomPromptInPack(promptPackCustomPrompts, promptIndex)"
                  >
                    Update
                  </SpinnerButton>
                </div>
                <div
                  class="prompt-item-property-cancel-custom button"
                  v-show="prompt.hasOwnProperty('isEditing') && prompt.isEditing === true"
                >
                  <SpinnerButton
                    class="create-custom-prompt-cancel-button"
                    :isProcessing="isSubmittingPrompt"
                    :showSpinner="false"
                    @click="stopEditingCustomPrompt(promptIndex)"
                  >
                    Cancel
                  </SpinnerButton>
                </div>
              </div>
              <div
                class="prompt-item"
                v-for="(prompt, promptIndex) in promptPackPublicPrompts"
                :key="'p'+promptIndex"
              >
                <div :class="[{ 'inactive': !prompt.active }, 'prompt-item-property-text']">
                  {{ transformPromptText(prompt.text) }} 
                </div>
                <div :class="[{ 'inactive': !prompt.active }, 'prompt-item-property-type']">
                  {{ parsePromptSetName(prompt.set) }}
                </div>
                <div :class="[{ 'inactive': !prompt.active }, 'prompt-item-property-type']">
                  {{ prompt.reply }} 
                </div>
                <div :class="[{ 'inactive': !prompt.active }, 'prompt-item-property-type']">
                  -------------
                </div>
                <toggle-button
                  :value="true"
                  color="#00B4A3"
                  :sync="true"
                  :height="toggleHeight"
                  :width="toggleWidth"
                  v-model="prompt.active"
                  :disabled="isSubmittingPrompt"
                  class="prompt-item-property-type"
                  style="display: flex; align-items: center;"
                  @change="togglePromptActivity('PUBLIC', promptPackPublicPrompts, promptIndex)"
                />
                <div class="prompt-item-property-overflow">
                  <PromptItemOverflow 
                    ref="publicOverflow"
                    @opened="stopCreatingPrompt(); stopEditingCustomPrompt(promptIndexBeingEdited);"
                    :disabled="isSubmittingPrompt"
                  >
                    <template v-slot:dropdown>
                      <ul class="dropdown">
                        <li>
                          <a @click="$refs['publicOverflow'][promptIndex].close(); duplicatePrompt(promptPackPublicPrompts, promptIndex);">
                            Duplicate
                          </a>
                        </li>
                        <li>
                          <a @click="$refs['publicOverflow'][promptIndex].close(); removePromptFromPack(promptPackPublicPrompts, promptIndex);" 
                          class="text-danger">
                            Remove
                          </a>
                        </li>
                      </ul>
                    </template>
                  </PromptItemOverflow>
                </div>
              </div>
            </div>
            <div
              class="main-prompt-message-container"
              v-show="showPack && selectedPromptPack.promptPackId !== null && promptPackCustomPrompts.length + promptPackPublicPrompts.length == 0"
            >
              <h1>This prompt pack is currently empty</h1>
              <h3>You can begin creating prompts for this pack above</h3>
            </div>
            <div class="subpage-container" v-if="!showPack">
              <div class="spinner-top-padded">
                <Spinner color="teal" />
              </div>
            </div>
            <div class="padding-row" >
            </div>
          </div>
        </div>
        <CreatePackModal
          v-if="showCreatePackModal"
          style="z-index: 100;"
          @prompt-pack-created="getPromptPacks"
          @close="showCreatePackModal = false"
        />
        <DeletePackModal
          v-if="showDeletePackModal"
          :promptPack="selectedPromptPack"
          style="z-index: 100;"
          @prompt-pack-deleted="getPromptPacks"
          @close="showDeletePackModal = false"
        />
        <RenamePackModal
          v-if="showRenamePackModal"
          :promptPack="selectedPromptPack"
          style="z-index: 100;"
          @prompt-pack-renamed="getPromptPacks"
          @close="showRenamePackModal = false"
        />
      </div>
    </transition>

    <div class="subpage-container" v-if="!show">
      <div class="spinner-top-padded">
        <Spinner color="teal" />
      </div>
    </div>

    <div class="right-subpage-container">
      <div v-if="show && showPack">
        <div
          class="prompt-message-container"
          v-show="selectedPromptPack.promptPackId !== null && 
            promptPackCustomPrompts.length + promptPackPublicPrompts.length > 0 && 
            numActivePrompts < 24"
        >
          <h2>Hey, you’re pretty good at this!</h2>
          <h3>But...you’ll need a total of 24 active prompts in this pack before it’s available for play.</h3>
          <h3>{{ promptCountMessage }}</h3>
          <h3>(If this is a frequent usage pack, we recommend at least 100 prompts.)</h3>
        </div>
        <div
          class="prompt-message-container"
          v-show="selectedPromptPack.promptPackId !== null && 
            numActivePrompts >= 24 && 
            promptPackCustomPrompts.length + promptPackPublicPrompts.length < 100"
        >
          <h2>This pack is available for play!</h2>
          <h3>You currently have {{numActivePrompts}} active prompts in this pack. </h3>
          <h3>(If this is a frequent usage pack, we recommend at least 100 prompts.)</h3>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Vue from 'vue';
import CreatePackModal from '@/components/promptViewComponents/CreatePackModal.vue';
import DeletePackModal from '@/components/promptViewComponents/DeletePackModal.vue';
import RenamePackModal from '@/components/promptViewComponents/RenamePackModal.vue';
import PromptItemOverflow from '@/components/promptViewComponents/PromptItemOverflow.vue';
import PromptPackCustomSelect from '@/components/utilComponents/PromptPackCustomSelect.vue';
import CustomSelect from '@/components/utilComponents/CustomSelect.vue';
import Spinner from '@/components/utilComponents/Spinner';
import SpinnerButton from '@/components/utilComponents/SpinnerButton';
import { ToggleButton } from 'vue-js-toggle-button';

export default Vue.extend({
  name: 'PromptPacksView',
  components: {
    CreatePackModal,
    DeletePackModal,
    RenamePackModal,
    CustomSelect,
    PromptPackCustomSelect,
    PromptItemOverflow,
    Spinner,
    SpinnerButton,
    ToggleButton
  },
  data: () => ({
    show: false,
    showPack: false,
    selectKey: 0, // this is to force rerender of the custom select component when the selectedPromptPack gets updated
    promptPackTable: {},
    promptPacks: [],
    selectedPromptPack: {},
    promptPackCustomPrompts: [],
    promptPackPublicPrompts: [],
    showCreatePackModal: false,
    showDeletePackModal: false,
    showRenamePackModal: false,

    // for creating prompts
    isCreatingPrompt: false,
    isSubmittingPrompt: false,    // flag to make sure the user can't do anything if they are updating the db
    characterLimit: 140,
    customPromptToCreate: {
      reply: 'text',
      type: 'static',
      text: '',
      injection: 'PLAYER_NAME'
    },

    // for editing prompts
    isEditingPrompt: false,
    promptIndexBeingEdited: null,

    // toggle button dimensions
    toggleHeight: 0,
    toggleWidth: 0
  }),
  computed: {
    auth0UserId: function () {
      return this.$auth.user.sub.replace(/\|/g, '_');
    },
    idToken: function () {
      return this.$auth.idToken;
    },
    numActivePrompts: function () {
      let count = 0;
      this.promptPackCustomPrompts.forEach(prompt => {
        if (prompt.active) {
          count += 1;
        }
      });
      this.promptPackPublicPrompts.forEach(prompt => {
        if (prompt.active) {
          count += 1;
        }
      });
      return count;
    },
    promptCountMessage: function() {
      const numTotalPrompts = this.promptPackCustomPrompts.length + this.promptPackPublicPrompts.length;
      return `You have a total of ${ numTotalPrompts } ${ numTotalPrompts === 1 ? 'prompt' : 'prompts' } in this pack, including ${ this.numActivePrompts } active ${ this.numActivePrompts === 1 ? 'prompt' : 'prompts' }.`;
    }
  },
  watch: {
    selectedPromptPack: function () {
      this.selectKey += 1;
    }
  },
  methods: {
    transformPromptText(promptText) {
      return promptText.replace("%PLAYER_NAME%", "[Player.Name]");
    },
    parsePromptSetName(set) {
      const promptSetName = set.split('#')[1];
      switch(promptSetName) {
        case 'PUBLIC':
          return 'Default';
        case 'CUSTOM':
          return 'Custom';
        default:
          return 'Default';
      }
    },
    async removePromptFromPack(prompts, promptIndex) {
      if (this.isSubmittingPrompt) {
        return;
      }
      
      this.stopCreatingPrompt();
      this.isSubmittingPrompt = true;
      try {
        await this.axios.post(`${ process.env.VUE_APP_BACKEND_API_URL }/removepromptfrompack`, {
          auth0UserId: this.auth0UserId,
          promptPackId: this.selectedPromptPack.promptPackId,
          promptPtrId: prompts[promptIndex].ptrId
        }, {
          headers: {
            'Content-Type': 'application/json', 
            'Authorization': `Bearer ${this.idToken}`
          }
        });
      } catch (error) {
        console.log('Error:', error);
      }

      this.getPromptsFromPack();
      this.isSubmittingPrompt = false;
    },
    async getPromptPacks() {
      try {
        const response = await this.axios.post(`${ process.env.VUE_APP_BACKEND_API_URL }/getpromptpacks`, {
          auth0UserId: this.auth0UserId,
        }, {
          headers: {
            'Content-Type': 'application/json', 
            'Authorization': `Bearer ${this.idToken}`
          }
        });
        this.promptPacks = response.data.promptPacks;
        this.selectedPromptPack = this.promptPacks[0] || { promptPackId: null, promptPackName: null };

        if (this.selectedPromptPack.promptPackId) {
          await this.getPromptsFromPack();
        }
      } catch (error) {
        console.log('Error:', error);
      }
    },
    async getPromptsFromPack() {
      this.showPack = false;
      this.promptPackCustomPrompts = [];
      this.promptPackPublicPrompts = [];
      await this.getCustomPromptsFromPack();
      await this.getPublicPromptsFromPack();
      this.showPack = true;
    },
    async getCustomPromptsFromPack () {
      try {
        const response = await this.axios.post(`${ process.env.VUE_APP_BACKEND_API_URL }/getpromptsfrompack`, {
          auth0UserId: this.auth0UserId,
          promptPackId: this.selectedPromptPack.promptPackId,
          promptSet: 'CUSTOM'
        }, {
          headers: {
            'Content-Type': 'application/json', 
            'Authorization': `Bearer ${this.idToken}`
          }
        });
        this.promptPackCustomPrompts = response.data.prompts;
      } catch (error) {
        console.log('Error:', error);
      }
    },
    async getPublicPromptsFromPack () {
      try {
        const response = await this.axios.post(`${ process.env.VUE_APP_BACKEND_API_URL }/getpromptsfrompack`, {
          auth0UserId: this.auth0UserId,
          promptPackId: this.selectedPromptPack.promptPackId,
          promptSet: 'PUBLIC'
        }, {
          headers: {
            'Content-Type': 'application/json', 
            'Authorization': `Bearer ${this.idToken}`
          }
        });
        this.promptPackPublicPrompts = response.data.prompts;
      } catch (error) {
        console.log('Error:', error);
      }
    },
    async startCreatingPrompt() {
      await this.getCustomPromptsFromPack();
      this.$refs.promptListContainer.scrollTo(0, 0);
      this.isCreatingPrompt = true;
      this.$nextTick(() => {
        this.$refs.createTextInput.focus();
      });
    },
    stopCreatingPrompt() {
      if (this.isSubmittingPrompt) {
        return;
      }

      this.isCreatingPrompt = false;
      this.customPromptToCreate.type = 'static';
      this.customPromptToCreate.reply = 'text';
      this.customPromptToCreate.text = '';
      this.$refs['customPromptReplySelector'].selected = 'text'
    },
    async createCustomPromptInPack() {
      if (this.isSubmittingPrompt || !this.customPromptToCreate.text || this.customPromptToCreate.text.length > this.characterLimit) {
        return;
      }

      this.isSubmittingPrompt = true;
      try {
        const response = await this.axios.post(`${ process.env.VUE_APP_BACKEND_API_URL }/createcustomprompt`, {
          auth0UserId: this.auth0UserId,
          promptType: this.customPromptToCreate.type,
          replyType: this.customPromptToCreate.reply,
          promptText: this.customPromptToCreate.text,
          injections: [this.customPromptToCreate.injection]
        }, {
          headers: {
            'Content-Type': 'application/json', 
            'Authorization': `Bearer ${this.idToken}`
          }
        });
      
        await this.axios.post(`${ process.env.VUE_APP_BACKEND_API_URL }/addprompttopack`, {
          auth0UserId: this.auth0UserId,
          promptPackId: this.selectedPromptPack.promptPackId,
          promptSet: 'CUSTOM',
          promptId: response.data.promptId
        }, {
          headers: {
            'Content-Type': 'application/json', 
            'Authorization': `Bearer ${this.idToken}`
          }
        });
      
      } catch (error) {
        console.log('Error:', error);
      }
      this.isSubmittingPrompt = false;
      this.stopCreatingPrompt();
      await this.getCustomPromptsFromPack();
    },
    async duplicatePrompt (prompts, promptIndex) {
      this.customPromptToCreate.type = 'static';
      this.customPromptToCreate.reply = prompts[promptIndex].reply;
      this.customPromptToCreate.text = prompts[promptIndex].text;
      await this.startCreatingPrompt();
    },
    async editCustomPrompt(promptIndex) {
      await this.getCustomPromptsFromPack();
      this.closeAllPromptOverflows();
      this.stopCreatingPrompt();
      this.isEditingPrompt = true;
      this.promptIndexBeingEdited = promptIndex;
      const selectedPrompt = this.promptPackCustomPrompts[promptIndex];
      this.$set(selectedPrompt, 'isEditing', true);
      this.$nextTick(() => {
        this.$refs['customTextInput'][promptIndex].focus();
        this.$refs['customReplySelect'][promptIndex].selected = this.promptPackCustomPrompts[promptIndex].reply;
      });
    },
    async stopEditingCustomPrompt(promptIndex) {
      if (promptIndex === null || this.isSubmittingPrompt) {
        // use a null check here since the prompt index can also be 0
        return;
      }

      await this.getCustomPromptsFromPack();
      const selectedPrompt = this.promptPackCustomPrompts[promptIndex];
      this.$set(selectedPrompt, 'isEditing', false);
      this.isEditingPrompt = false;
      this.promptIndexBeingEdited = null;
    },
    async updateCustomPromptInPack(prompts, promptIndex) {
      if(this.isSubmittingPrompt || !prompts[promptIndex].text.length || prompts[promptIndex].text.length > this.characterLimit) {
        return;
      }

      this.isSubmittingPrompt = true;
      try {
        await this.axios.post(`${ process.env.VUE_APP_BACKEND_API_URL }/editcustomprompt`, {
          auth0UserId: this.auth0UserId,
          promptId: prompts[promptIndex].promptId,
          promptType: prompts[promptIndex].type,
          replyType: prompts[promptIndex].reply,
          promptText: prompts[promptIndex].text,
          injections: '',
        }, {
          headers: {
            'Content-Type': 'application/json', 
            'Authorization': `Bearer ${this.idToken}`
          }
        });
      } catch (error) {
        console.log(error);
      }
      await this.getCustomPromptsFromPack();
      this.isSubmittingPrompt = false;
      this.isEditingPrompt = false;
    },
    async togglePromptActivity (promptSet, prompts, promptIndex) {
      try {
        await this.axios.post(`${ process.env.VUE_APP_BACKEND_API_URL }/togglepromptactivity`, {
          auth0UserId: this.auth0UserId,
          promptSet,
          promptId: prompts[promptIndex].promptId
        }, {
          headers: {
            'Content-Type': 'application/json', 
            'Authorization': `Bearer ${this.idToken}`
          }
        });
      } catch (error) {
        console.log(error.body);
      }
    },
    calcToggleSize() {
      this.toggleHeight = Math.min(document.documentElement.clientHeight, document.documentElement.clientWidth) / 33;
      this.toggleWidth = this.toggleHeight * 2.27;
    },
    closeAllPromptOverflows() {
      if (this.$refs['customOverflow']) {
        this.$refs['customOverflow'].forEach(overflow => {
          overflow.close();
        });
      }
      if (this.$refs['publicOverflow']) {
        this.$refs['publicOverflow'].forEach(overflow => {
          overflow.close();
        });
      }
    }
  },
  mounted: async function() {
    this.$nextTick(function() {
      window.addEventListener('resize', this.calcToggleSize);
      this.calcToggleSize();
    });
    try {
      await this.getPromptPacks();
      this.show = true;
    } catch (error) {
      console.log('Error:', error);
    }
  }
})
</script>

<style scoped lang="scss">
.subpage-wrapper {
  display: flex;
  max-height: 100%;
}

.subpage-container {
  margin: 0 auto;
  width: 60em;
}

.inner-container {
  display: flex;
  flex-direction: column;
  height: 85vh;
}

.right-subpage-container {
  padding-top: 3.3em;
  width: 11em;
  margin-left: 0.4em;
}

.type-selector-container {
  display: flex;
  flex-direction: row;
  // justify-content: space-between;
  max-width: 100%;
  margin: 0 0.8em 0.4em;
  font-size: 0.8em;
}

.pack-select-wrapper {
  width: 24em;
}

.prompts-container {
  width: 50%;
}

.main-prompt-message-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  width: 50em;
  height: 40em;
  margin: 0 auto;
  font-size: 0.9em;

  & > h1 {
    color: $brand-teal;
  }

  & a {
    color: $lilac-purple;
    &:hover {
      filter: brightness(1.15);
    }
  }
}

.prompt-message-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  font-size: 0.7em;
  border-radius: 0.8em;
  padding: 0 1.2em;
  background: $baby-blue;
  color: $eigengrau;

  & > h2 {
    color: $violet;
  }
}

.prompt-list-container {
  height: 80vh;
  width: 100%;
  overflow: scroll;
  overflow-x: hidden;
  list-style-type: none;
  font-size: 0.8em;
}

.prompt-list {
  position: relative;
  border-radius: 0.4em;
  color: $smoke-gray;
}

.prompt-item {
  position: relative;
  display: flex;
  margin: 0 0.8em;
  padding: 0.4em 1.6em;
  border: 0.1em solid $light-gray;
  height: 4.4em;
  color: $smoke-gray;
  background-color: white;

  &-button {
    height: max-content;
    width: max-content;
    background-color: $brand-gold;
    text-align: center;
    padding: 0.4em 0.8em;
    margin: 0.4em auto;
    font-size: 1.2em;
    border-radius: 0.2em;
    box-shadow: 0 1px 1px 1px $light-gray;
    user-select: none;
    cursor: pointer;

    &.edit {
      color: $natural-white;
      background-color: $lilac-purple;
      z-index: 10;
    }

    &.danger {
      color: $natural-white;
      background-color: $crimson;
      z-index: 10;
    }
  }

  &-button:hover {
    background-color: $light-lilac-purple;
  }

  &-to-create {
    color: $brand-teal!important;
    &:hover {
      cursor: pointer;
    }
  }

  &-property {
    padding: 0.4em 0.8em;
    justify-content: center;
    
    &-text {
      display: flex;
      align-items: center;
      flex: 2;
      width: 50%;
      left: 25%;
      user-select: none;
      transition: color 0.5s;
      white-space: nowrap;
      overflow-x: auto;

      &.no-overflow {
        overflow-x: revert!important;
      }
    }

    &-hidden {
      color: $off-white;
    }
    &-type {
      display: flex;
      justify-content: center;
      align-items: center;
      width: 10%;
      text-transform: capitalize;
      user-select: none;
      transition: color 0.5s;
    }
    &-last-edited {
      display: block;
      align-items: center;
      width: 10%;
      padding-top: 0.4em;
      text-align: center;
      user-select: none;
      transition: color 0.5s;
      white-space: nowrap; 
      overflow: hidden;
      text-overflow: ellipsis; 
    }
    &-overflow {
      display: flex;
      justify-content: center;
      width: 2%;
      margin-left: 0.4em;
    }
    &-create-custom {
      display: flex;
      justify-content: center;
    }
    &-cancel-custom {
      display: flex;
      justify-content: center;
      width: 6%;

    }
  }
}

.top-row {
  font-weight: bold;
  border-radius: 0.8em 0.8em 0 0;
}

.prompt-item + .prompt-item:last-of-type {
  border-radius: 0 0 0.8em 0.8em;
}

.button {
  display: flex;
  justify-content: center;
  align-items: center;
  white-space: nowrap;
  text-align: center;
  user-select: none;
}

.delete-pack-button {
  background-color: $crimson;
  color: $natural-white;
  padding: 0 0.8em;
  margin: 0.2em 0.2em;
  margin-left: 0.4em;
  width: max-content;
  font-size: 1.2em;
  border-radius: 0.4em;
  box-shadow: 0 1px 1px 1px $light-gray;
  cursor: pointer;
}

.delete-pack-button:hover {
  filter: brightness(1.15);
}

.create-pack {
  &-text {
    margin: 0 0.4em;
    margin-left: auto;
  }
  &-button {
    color: $natural-white;
    background-color: $brand-teal;
    padding: 0 0.8em;
    margin: 0.2em 0 0.2em auto;
    width: max-content;
    font-size: 1.2em;
    border-radius: 0.4em;
    box-shadow: 0 1px 1px 1px $light-gray;
    cursor: pointer;
    
  }
  &-button:hover {
    filter: brightness(1.15);
  }
}

// to disable buttons on this page
.button-disabled {
  background-color: $light-gray!important;
  box-shadow: 0 1px 1px 1px $bright-gray!important;
  pointer-events: none;
}

.disabled {
  pointer-events: none;
}

.inactive {
  color: $light-gray;
}

.dropdown {
  position: absolute;
  right: 0px;
  z-index: 20;
  background: white;
  border: 1px solid $smoke-gray;
  border-radius: 0.4em;
  box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.16), 0 2px 8px 0 rgba(0, 0, 0, 0.12);
  margin: 0;
  padding: 0;

  li {
    display: block;
    width: 100%;
    padding: 0;
    margin: 0;
    a {
      width: 100%;
      padding: 1em 18px;
      margin: 0;
      display: inline-block;
      white-space: pre;
      box-sizing: border-box;
      user-select: none;
      &:hover {
        background: $lilac-purple;
        color: white;
        cursor: pointer;
      }
    }
  }
}

.create-custom-prompt {
  &-button {
    display: flex;
    justify-content: center;
    height: max-content;
    background-color: $brand-teal;
    color: $natural-white;
    padding: 0.4em 0.6em;
    margin: 0.2em 0.6em;
    font-size: 1.2em;
    border-radius: 0.2em;
    box-shadow: 0 1px 1px 1px $light-gray;
    cursor: pointer;
  }
  &-button:hover {
    background-color: $light-teal;
  }
  &-cancel-button {
    height: max-content;
    background-color: $crimson;
    color: $natural-white;
    padding: 0.4em 0.6em;
    margin: 0.2em 0.6em;
    font-size: 1.2em;
    border-radius: 0.2em;
    box-shadow: 0 1px 1px 1px $light-gray;
    cursor: pointer;
  }
  &-cancel-button:hover {
    background-color: $light-crimson;
  }
  &-to-create-text {
    &.static {
      width: 100%;
      font-family: inherit;
      font-size: 1em;
      border: 0.1em solid $brand-teal;
      border-radius: 0.4em;
      padding: 0.5em 0.8em;
    }
  }
  &-character-limit {
    font-size: 0.8em;
    padding: 0.4em 0.8em;
    text-align: center;

    &.invalid {
      color: $crimson;      
    }
  }
}

.text-danger {
  color: $crimson;
}

.padding-row {
  height: 10em;
}
</style>
